import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import useCortexSession from "./useCortexSession"
import useKnowledgeBases from "./useKnowledgeBases"
import useConversationHistory from "./useConversationHistory"
import useBalance from "./useBalance"
import useAssistantLock from "./useAssistantLock"
import { askQuestion, askQuestionStreaming, parseAnswerSources } from "./cortexQuestions"
import { consumePendingQuickAsk, registerQuickAskListener, unregisterQuickAskListener } from "./pendingQuickAsk"
import reportCuradoria from "./reportCuradoria"
import defaultReducer from "../../utils/defaultReducer"
import useNotification from "../Notification"
import { registerModuleUsage } from "../../utils/useModuleAccess"

const MAX_MESSAGE_LENGTH = 1000

// Composes the shared Cortex hooks into the state/handlers a chat surface
// needs. `rawExternalId` is the ticket's uniqueid for the sidebar, or the
// persistent per-login tab id for the fullTab surface. `useTicketLock`
// enables the persistent per-uniqueid lock (sidebar only - see
// specs/cortex-native-chat: "Per-Ticket Asking Lock").
const useCortexChat = ({ rawExternalId, useTicketLock = false, canSendMessage = true }) => {
    const session = useCortexSession()
    const { assistantUrl, accessToken, accountcode, userId, streamMode } = session

    const kb = useKnowledgeBases({ assistantUrl, accessToken, accountcode })
    const { fetchHistory } = useConversationHistory({ assistantUrl, accessToken })
    const balance = useBalance({ assistantUrl, accessToken, accountcode })
    const [ticketLocked, lockTicket, unlockTicket] = useAssistantLock(useTicketLock ? rawExternalId : null)

    const [state, setState] = useReducer(defaultReducer, {
        messages: [],
        text: '',
        loadingHistory: false,
        awaitingAnswer: false,
        showKbPicker: false,
    })

    const messagesRef = useRef(state.messages)
    messagesRef.current = state.messages

    const activeExternalId = (kb.selectedBase && rawExternalId) ? `${kb.selectedBase}${rawExternalId}` : null

    const loadHistory = useCallback(async () => {
        if (!kb.selectedBase || !rawExternalId) {
            setState({ messages: [] })
            return
        }
        setState({ loadingHistory: true })
        const messages = await fetchHistory(kb.selectedBase, rawExternalId)
        setState({ loadingHistory: false, messages })
    }, [kb.selectedBase, rawExternalId, fetchHistory])

    useEffect(() => { loadHistory() }, [loadHistory])

    const pushMessage = useCallback((id, text, msgUserId, extra = {}) => {
        const next = [...messagesRef.current, { id, text, userId: msgUserId, ...extra }]
        messagesRef.current = next
        setState({ messages: next })
    }, [])

    const updateLastMessage = useCallback((id, text) => {
        const next = messagesRef.current.map((m) => (m.id === id ? { ...m, text } : m))
        messagesRef.current = next
        setState({ messages: next })
    }, [])

    const selectKb = useCallback((id, isFile = false) => {
        kb.selectBase(id, isFile)
    }, [kb])

    const submit = useCallback(async (message) => {
        if (!message) {
            useNotification('Ops', 'Não é possível enviar uma mensagem vazia.', 'danger')
            return
        }
        if (message.length > MAX_MESSAGE_LENGTH) {
            useNotification('Ops', `Mensagem muito longa, o texto não deve passar de ${MAX_MESSAGE_LENGTH} caracteres. Por favor, tente novamente.`, 'danger')
            return
        }
        if (!activeExternalId) return

        pushMessage(0, message, 1)
        setState({ text: '', awaitingAnswer: true })
        if (useTicketLock) lockTicket()

        const askArgs = {
            assistantUrl,
            accessToken,
            question: message,
            selectedBase: kb.selectedBase,
            fileConversation: kb.fileConversation,
            userId,
            externalId: activeExternalId,
        }

        try {
            if (streamMode) {
                await askQuestionStreaming({
                    ...askArgs,
                    // The stream is raw text, not JSON - there's no separate
                    // structured RAG-sources field to attach here (unlike
                    // the non-streaming path below). Curadoria still gets
                    // the question/answer, just without sources for
                    // streamed answers.
                    onStart: (uuid, text) => {
                        registerModuleUsage("cortex")
                        pushMessage(uuid, text, 0, { question: message, sources: [] })
                        setState({ awaitingAnswer: false })
                        if (useTicketLock) unlockTicket()
                    },
                    onUpdate: (uuid, text) => updateLastMessage(uuid, text),
                    onDone: (uuid, text) => {
                        updateLastMessage(uuid, text)
                        if (useTicketLock) {
                            reportCuradoria({ uniqueid: rawExternalId, question: message, answer: text, sources: [] })
                        }
                    },
                })
                // The stream itself carries no balance figure (it's raw
                // text, not JSON) - refetch instead of waiting up to 60s
                // for the next poll.
                balance.refresh()
            } else {
                const data = await askQuestion(askArgs)
                registerModuleUsage("cortex")
                const sources = parseAnswerSources(data.response.answer_sources)
                pushMessage(data.response.id, data.response.answer, 0, { question: message, sources })
                setState({ awaitingAnswer: false })
                if (useTicketLock) unlockTicket()
                if (typeof data.balance === 'number') balance.applyRemaining(data.balance)
                if (useTicketLock) {
                    reportCuradoria({ uniqueid: rawExternalId, question: message, answer: data.response.answer, sources })
                }
            }
        } catch (err) {
            console.error(err)
            useNotification('Ops', 'Algo deu errado, tente novamente.', 'danger')
            setState({ awaitingAnswer: false })
            if (useTicketLock) unlockTicket()
        }
    }, [activeExternalId, rawExternalId, assistantUrl, accessToken, kb.selectedBase, kb.fileConversation, userId, streamMode, useTicketLock, lockTicket, unlockTicket, pushMessage, updateLastMessage, balance.refresh, balance.applyRemaining])

    // Picks up a question left by the ticket message list's "ask Cortex
    // about this message" button (see pendingQuickAsk.js) - only ever
    // matches for the sidebar, since only ticket uniqueids are ever left
    // there. Handles both the case where this surface just mounted (a
    // message was already waiting) and the case where it was already open
    // (registers a listener so a second quick-ask click, which doesn't
    // change any of this effect's deps, still gets delivered).
    useEffect(() => {
        if (!activeExternalId || !rawExternalId) return

        const text = consumePendingQuickAsk(rawExternalId)
        if (text) submit(text)

        registerQuickAskListener(rawExternalId, submit)
        return () => unregisterQuickAskListener(rawExternalId)
    }, [activeExternalId, rawExternalId, submit])

    const [showKbPicker, setShowKbPicker] = useState(false)

    // Reports one of the four answer-action buttons (send now / copy to
    // edit / mark good / mark bad) for curadoria - see spec_curadoria.md.
    // Sidebar only: fullTab's rawExternalId is a synthetic per-login tab id,
    // not a real ticket, so there's nothing meaningful to record there.
    const reportAction = useCallback((message, action) => {
        if (!useTicketLock || !rawExternalId) return
        reportCuradoria({
            uniqueid: rawExternalId,
            question: message.question,
            answer: message.text,
            sources: message.sources,
            action,
        })
    }, [useTicketLock, rawExternalId])

    return {
        ready: session.ready,
        enterpriseHasAssistant: session.enterpriseHasAssistant,
        userHasAssistant: session.userHasAssistant,
        assistantUrl,
        accessToken,

        loadingKb: kb.loading,
        knowledgeBases: kb.knowledgeBases,
        selectedBase: kb.selectedBase,
        kbName: kb.knowledgeBases[kb.selectedBase]?.parsed_name,
        selectKb,
        showKbPicker,
        setShowKbPicker,

        messages: state.messages,
        loadingHistory: state.loadingHistory,
        text: state.text,
        setText: (text) => setState({ text }),

        submit,
        locked: state.awaitingAnswer || (useTicketLock && ticketLocked),
        canSendMessage,
        activeExternalId,
        reportAction,

        packages: balance.packages,
        maxPackages: balance.maxPackages,
    }
}

export default useCortexChat
